import { useReducer, useContext } from 'react';
import AuthContext from './AuthContext';
import AuthReducer from './AuthReducer';
import SeedTypesApiService from '../../services/apiServices/SeedTypesApiService';

import {
  RESET,
  SET_CURRENT_USER,
  SET_CURRENT_CONTAINER,
  SET_CURRENT_TENANT,
  SET_ACCESSIBLE_CONTAINERS,
  SET_ACCESSIBLE_TEMPLATES,
  SET_ACCESSIBLE_SEEDTYPES,
  UPDATE_USER_CONTEXT,
  UPDATE_ACCESSIBLE_TENANTS,
  SET_LOADING_SEEDTYPES
} from '../types/contextTypes';
import {
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession,
  signOut as amplifySignOut,
  signInWithRedirect
} from 'aws-amplify/auth';
import { awsconfig } from '../../aws-config/awsconfig';
import { GLOBAL_ADMIN } from '../../constants/roles/roles';
import { jwtDecode } from 'jwt-decode';
import UsersApiService from '../../services/apiServices/UsersApiService';
import TenantsApiService from '../../services/apiServices/TenantsApiService';
import ContainersApiService from '../../services/apiServices/ContainersApiService';
import SessionInformationContext from '../session-information/SessionInformationContext';

export default (props) => {
  const initialState = {
    userInformation: null,
    currentTenant: null,
    currentContainer: null,
    currentRoles: null,
    isAuthenticated: false,
    loading: false,
    accessibleTenants: [],
    accessibleContainers: [],
    accessibleSeedTypes: [],
    accessibleTemplates: [],
    sessionInformation: null,
    isGlobalAdmin: false,
    seedTypesLoading: false
  };

  const { sessionInformation, setSessionInformation } = useContext(SessionInformationContext);

  const auth = async () => {
    try {
      const user = await amplifyGetCurrentUser();
      const session = await fetchAuthSession();

      if (!session?.tokens?.idToken) {
        throw new Error('No idToken found.');
      }

      const idToken = session.tokens.idToken.toString();
      const attributes = jwtDecode(idToken);

      const cognitoUser = {
        userId: user.username,
        firstName: attributes.given_name,
        lastName: attributes.family_name,
        email: attributes.email,
        userPoolId: awsconfig.Auth.userPoolId,
      };

      let newUserState = { ...initialState };
      newUserState.userInformation = cognitoUser;
      newUserState.accessibleTenants = await loadAccessibleTenantsAsync(cognitoUser.userId);

      const dbUser = await getUser(cognitoUser.userId);
      const userRoleResponse = await UsersApiService.getUserRole(dbUser?.id);

      const currentSessionInformation = sessionInformation;

      if (currentSessionInformation?.lastSelectedTenantId) {
        newUserState.currentTenant = newUserState.accessibleTenants.find(
          (t) => t.id === currentSessionInformation.lastSelectedTenantId
        );
      }

      if (!newUserState.currentTenant) {
        newUserState.currentTenant = newUserState.accessibleTenants[0];
      }

      if (!newUserState.currentTenant) {
        console.error('No current tenant found.');
        return;
      }

      newUserState.accessibleContainers =
        await getAccessibleContainersAndCurrentContainer(newUserState.currentTenant);

      newUserState.accessibleTemplates =
        await getAccessiblePrinterTemplates(newUserState.currentTenant.id);

      if (currentSessionInformation?.lastSelectedContainerId) {
        newUserState.currentContainer =
          newUserState.accessibleContainers?.find(
            (c) => c.id === currentSessionInformation.lastSelectedContainerId
          );
      }

      if (!newUserState.currentContainer && newUserState.accessibleContainers.length > 0) {
        newUserState.currentContainer = newUserState.accessibleContainers[0];
      }

      newUserState.accessibleSeedTypes =
        await loadAccessibleSeedTypesAsync(
          newUserState.currentTenant,
          newUserState.currentContainer,
          true,
          'seedVariantTypes'
        );

      const roles =
        await getUserRoleForTenant(newUserState.currentTenant.id, cognitoUser.userId);

      newUserState.currentRoles = roles;
      newUserState.isGlobalAdmin =
        userRoleResponse?.data?.data.role === GLOBAL_ADMIN;

      newUserState.isAuthenticated = true;

      dispatch({
        type: UPDATE_USER_CONTEXT,
        payload: newUserState,
      });

    } catch (err) {
      console.error('Auth error:', err);
      await amplifySignOut();
      reset();
    }
  };

  const [state, dispatch] = useReducer(AuthReducer, initialState);

  const reloadAccessibleTenants = async (userId) => {
    var tenants = await loadAccessibleTenantsAsync(userId)
      .then((listOfTenants) => {
        dispatch({
          type: UPDATE_ACCESSIBLE_TENANTS,
          payload: listOfTenants,
        });
        return listOfTenants;
      });

    return tenants;
  };

  const loadAndSetAccessibleTemplatesAsync = async (tenant) => {
    const templates = await getAccessiblePrinterTemplates(tenant.id);
    dispatch({
      type: SET_ACCESSIBLE_TEMPLATES,
      payload: templates,
    });
  };

  const loadAccessibleSeedTypesAsync = async (tenant, container, includeOutdated = true, include = null) => {
    dispatch({
      type: SET_LOADING_SEEDTYPES,
      payload: true
    });

    if (!container?.id || !tenant?.id) {
      return [];
    }
    try {
      const response = await SeedTypesApiService.getAllSeedTypes(tenant?.id, container.id, includeOutdated, include);
      const seedTypes = response.data.data;
      return seedTypes;
    } catch (error) {
      console.error(error);
    }
  };

  const loadAndSetAccessibleSeedTypesAsync = async (tenant, container, includeOutdated = true, include = null) => {
    dispatch({
      type: SET_LOADING_SEEDTYPES,
      payload: true
    });
    const response = await SeedTypesApiService.getAllSeedTypes(tenant?.id, container.id, includeOutdated, include);
    const seedTypes = response.data.data;
    dispatch({
      type: SET_ACCESSIBLE_SEEDTYPES,
      payload: seedTypes,
    });
    return seedTypes;
  };

  const updateContainer = async (container) => {
    try {
      if (!container?.id || !state.currentTenant?.id) {
        console.error('Container ID or Tenant ID is missing for updating container.');
        return;
      }

      const body = {
        data: {
          id: container?.id,
          name: container?.name,
          description: container?.description,
          farmHandName: (!container?.farmHandName || container.farmHandName === '') ? null : container.farmHandName,
          cameras: container?.cameras,
          street: container?.location?.street,
          number: container?.location?.number,
          postalCode: container?.location?.postalCode,
          city: container?.location?.city,
          country: container?.location?.country,
          latitude: container?.location?.latitude,
          longitude: container?.location?.longitude
        },
      };

      await ContainersApiService.updateContainer(state.currentTenant?.id, container.id, body);
    } catch (e) {
      console.error('Error updating container:', e);
    } finally {
      await loadAndSetAccessibleContainersAsync();
    }
  };

  const loadAndSetAccessibleContainersAsync = async (tenant) => {
    if (!tenant?.id) {
      return [];
    }

    if (!tenant?.id == state.currentTenant?.id) {
      return state.accessibleContainers;
    }

    const newAccessibleContainers = await getAccessibleContainersAndCurrentContainer(tenant);
    dispatch({
      type: SET_ACCESSIBLE_CONTAINERS,
      payload: newAccessibleContainers
    });
    return newAccessibleContainers;
  };

  const getAccessibleContainersAndCurrentContainer = async (tenant) => {
    if (!tenant?.id) {
      console.error('No tenant ID provided for fetching accessible containers.');
      return null;
    }
    try {
    var responseGetAccessibleContainers = await ContainersApiService.getAccessibleContainersByTenantId(tenant.id);
    return responseGetAccessibleContainers.data.data
    } catch (error) {
      console.error('Error fetching accessible containers:', error);
    }
  };

  const setAccessibleContainers = async (tenant) => {
    const result = await getAccessibleContainersAndCurrentContainer(tenant);

    dispatch({
      type: SET_ACCESSIBLE_CONTAINERS,
      payload: result,
    });

    return result;
  };

  const setCurrentUser = (user) => {
    dispatch({
      type: SET_CURRENT_USER,
      payload: user,
    });
  };

  const setCurrentTenant = async (newTenant, containerId = null) => {
    if (!newTenant?.id) {
      return null;
    }
    const newRoles = await getUserRoleForTenant(newTenant.id, state.userInformation.userId);
    const newContainers = await getAccessibleContainersAndCurrentContainer(newTenant);
    var newCurrentContainer = null;
    const givenContainer = newContainers.find(c => c.id === containerId);
    if (givenContainer){
      newCurrentContainer = givenContainer;
    }else{
      newCurrentContainer = newContainers.length < 1 ? null : newContainers[0];
    }
    const newSessionInformation = { ...sessionInformation, lastSelectedTenantId: newTenant.id, lastSelectedContainerId: newCurrentContainer == null ? null : newCurrentContainer.id };

    setSessionInformation(newSessionInformation);

    const newSeedTypes = await loadAccessibleSeedTypesAsync(newTenant, newCurrentContainer, true, 'seedVariantTypes');
    const newTemplates = await getAccessiblePrinterTemplates(newTenant.id);
    if (state.currentTenant.id !== newTenant.id) {
      dispatch({
          type: SET_CURRENT_TENANT,
          payload: {
              newContainers: newContainers,
              newCurrentContainer: newCurrentContainer,
              newSeedTypes: newSeedTypes,
              newTemplates: newTemplates,
              newRoles: newRoles,
              newCurrentTenant: newTenant,
          },
      });
  } else {
      console.log("Current tenant remains unchanged.");
  }
  return newTenant;
  };

  const setCurrentContainer = async (container) => {
    if (!container?.id) {
      return null;
    }

   const newSessionInformation = {...sessionInformation, lastSelectedTenantId: state.currentTenant?.id, lastSelectedContainerId: container.id};
   setSessionInformation(newSessionInformation);
    const newSeedTypes = await loadAccessibleSeedTypesAsync(state?.currentTenant, container, true, 'seedVariantTypes');


    const payload = {
      newSeedTypes: newSeedTypes == undefined ? [] : newSeedTypes,
      newCurrentContainer: container,
    };

    dispatch({
      type: SET_CURRENT_CONTAINER,
      payload: payload,
    });
    return true;
  };

  const reset = () => {
    dispatch({
      type: RESET,
      payload: initialState,
    });
  };

  const signIn = async () => {
    await signInWithRedirect();
  };

  const signOut = async () => {
    await retrySignOutWithErrorHandling();
  };

  const retrySignOutWithErrorHandling = async (maxRetries = 3, delay = 1000) => {
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        await amplifySignOut();
        console.log('Sign out successful.');
        return;
      } catch (error) {
        attempt++;
        if (error.message === 'Signout timeout fail') {
          console.log('Network issue detected, retrying...');
        } else {
          console.error('Sign out failed due to other error:', error);
        }

        if (attempt >= maxRetries) {
          console.error('Sign out failed after maximum retries:', error);
        }

        console.log(`Retrying sign out... Attempt ${attempt}`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2;
      }
    }
  }

  const setAccessibleSeedTypes = async (container, tenant = null) => {
    try {
      var result;
      if (container) {
        result = await SeedTypesApiService.getAllSeedTypes(tenant == null ? state.currentTenant?.id : tenant.id, container?.id);
      } else if (state?.currentContainer) {
        result = await SeedTypesApiService.getAllSeedTypes(state.currentTenant?.id, state?.currentContainer?.id);
      } else {
        return;
      }
      dispatch({
        type: SET_ACCESSIBLE_SEEDTYPES,
        payload: result?.data?.data,
      });
      return result?.data?.data;
    } catch (error) {
      console.error('Failed to load accessible seed types.', error);
    }
  };

  // Methodes to handle ApiService calls.
  const updateGlobalAdminRole = async (userId, willBeGlobalAdmin) => {
    if (!userId) {
      console.error('No user ID provided for updating global admin role.');
      return;
    }

    await UsersApiService.updateGlobalAdminRole(userId, willBeGlobalAdmin)
  };

  const loadAccessibleTenantsAsync = async (userId) => {
    if (!userId) {
      console.error('No user provided for loading accessible tenants.');
      return;
    }

    try {
      const responseAccessibleTenants = await UsersApiService.getAllAccessibleTenantsByUserId(userId);
      const accessibleTenants = responseAccessibleTenants.data.data;
      if (!Array.isArray(accessibleTenants) || accessibleTenants.length === 0) {
        return Promise.resolve([]);
      }

      const sortedTenants = accessibleTenants.sort((a, b) => a.name.localeCompare(b.name));
      return sortedTenants;
    } catch (err) {
      console.error('Error while loading tenants:', err);
    }
  };

  const loadTenantUsers = async (tenantId) => {
    if (!tenantId) {
      return;
    }
    //1 Get all users ids for tenant.
    try {
    await TenantsApiService.getAllTenantUsers(tenantId);
    } catch (error) {
      console.error('Error fetching users for tenant:', error);
      return;
    }
    //2 Load the user for each id;
    const usersWithRoles = [];
    for (const userId of userIdsInTenant) {
      //2b ...and get the user's role.
      let roleForThisUser = await getUserRoleForTenant(tenantId, userId);
      usersWithRoles.push(roleForThisUser);
    }

    return usersWithRoles;
  };

  const getUser = async (userId) => {
    if (!userId) {
      console.error('No user ID provided for fetching user information.');
      return null;
    }
    try {
    const response = await UsersApiService.getUser(userId);
    return response?.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      return null;
    }
  };

  const getAccessiblePrinterTemplates = async (tenantid) => {
    if (!tenantid) {
      console.error('No tenant ID provided for fetching accessible printer templates.');
      return null;
    }
    try {
    var responseGetPrinterTemplates = await TenantsApiService.getAccessiblePrinterTemplatesByTenantId(tenantid);
    return responseGetPrinterTemplates?.data?.data?.labelPrinterTemplates;
    } catch (error) {
      console.error('Error fetching accessible printer templates:', error);
      return;
    }
  }

  const getUserRoleForTenant = async (tenantId, userId) => {
    if (!tenantId || !userId) {
      console.error('Tenant ID or User ID is missing for fetching user roles.');
      return null;
    }
    try {
    var roles = await TenantsApiService.getUserRoleForTenant(tenantId, userId);
    return roles.data.data.roles;
    } catch (error) {
      console.error('Error fetching user role for tenant:', error);
      return;
    }
  }

  const setAccessibleTemplates = async () => { };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        setCurrentUser,
        loadAndSetAccessibleTemplatesAsync,
        loadAndSetAccessibleSeedTypesAsync,
        loadAndSetAccessibleContainersAsync,
        loadTenantUsers,
        reset,
        loadAccessibleTenantsAsync,
        reloadAccessibleTenants,
        signIn,
        signOut,
        setCurrentContainer,
        setCurrentTenant,
        setAccessibleContainers,
        setAccessibleSeedTypes,
        setAccessibleTemplates,
        setAccessibleContainersAndCurrentContainer: getAccessibleContainersAndCurrentContainer,
        updateContainer,
        updateGlobalAdminRole,
        auth,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
